import styled, { css } from "styled-components/macro";
import colors, { shadows, transition } from "constants/colors";
import { maxQuery, minQuery, SizeMax, SizeMin } from "constants/media";
import Icon from "components/atoms/Icon";

interface HalfCircleProps {
  background: keyof typeof colors;
  position: "top" | "bottom";
}

export const HalfCircle = styled.div<HalfCircleProps>`
  width: 8px;
  height: 8px;
  position: absolute;
  left: 0;
  border: 1px solid ${colors.richElectricBlue};
  border-radius: 50%;
  background-color: ${({ background }) => colors[background]};
  cursor: pointer;

  ${({ position }) =>
    position === "top"
      ? css`
          top: 0;
        `
      : css`
          bottom: 0;
        `};
`;

export const HalfCircles = styled.div`
  position: absolute;
  width: 8px;
  height: calc(100% + 8px);
  left: 32px;
  top: 50%;
  transform: translateY(-50%);
  overflow: hidden;
  cursor: pointer;
`;

export const Dash = styled.div`
  width: 1px;
  height: 15px;
  border-left: 1px dashed ${colors.richElectricBlue};
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

export const Logo = styled.div`
  position: absolute;
  left: 7px;
  top: 50%;
  transform: translate(0, -50%);
`;

export const Text = styled.div`
  white-space: nowrap;
  display: inline-block;
  flex: 1;
  font-weight: 500;
  max-width: 204px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${colors.richElectricBlue};
  font-size: 14px;
  line-height: 16px;
`;

interface CopyTooltipPopProps {
  copied?: boolean;
}
export const CopyTooltipPop = styled.span<CopyTooltipPopProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0.5em 0.25em;
  border-radius: 5px;
  position: absolute;
  bottom: 100%;
  right: 0;
  z-index: 2;
  background: #2a97e0;
  color: white;
  box-shadow: 0 0 0 5px rgb(0 0 0 / 5%);
  padding: 0.5em 0.75em;
  transform: translateY(0);
  opacity: 0;
  transition: transform 1s ease-out, opacity 0.05s ease-in;

  ${({ copied }) =>
    copied &&
    css`
      animation: popup 0.75s;
    `}

  @keyframes popup {
    to {
      transform: translateY(-15px);
      opacity: 1;
      animation-play-state: paused;
    }
  }
`;

export const Container = styled.div`
  display: flex;
  align-items: center;
  border-radius: 5px;
  border: 1px solid ${colors.richElectricBlue};
  padding-right: 7px;
  cursor: pointer;
  transition: ${transition.duration.medium};
  position: relative;
  overflow: hidden;
  padding-top: 0.4375em;
  padding-bottom: 0.4375em;

  &:hover {
    box-shadow: ${shadows.lv2};
  }
`;

// Large Land
const Line = styled.div``;

export const HeadLine = styled(Line)`
  display: flex;
  justify-content: space-between;
`;

export const CopyCode = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${colors.gray};
  font-weight: 600;
`;

export const CopyCodeIcon = styled(Icon)`
  margin-left: 11px;
`;

export const DescLine = styled(Line)`
  color: ${colors.gray};
  font-size: 12px;
`;

export const GrayText = styled(Line)`
  color: ${colors.gray};
`;

export const DateLine = styled(GrayText)`
  font-size: 14px;
`;

interface OutsideWrapContainerProps { }
export const OutsideWrapContainer = styled.div<OutsideWrapContainerProps>`
  position: relative;
`;

interface WrapContainerProps {
  type: "small" | "large";
  selected?: boolean;
}
export const WrapContainer = styled.div<WrapContainerProps>`
  position: relative;
  overflow: hidden;
  min-height: 29px;

  ${Container} {
    z-index: 1;
    ${minQuery(
  SizeMin.MSmall,
  css`
        height: 32px;
      `,
)};
  }
  ${HalfCircles} {
    z-index: 2;
  }

  ${({ selected }) =>
    selected
      ? css`
      background: ${colors.richElectricBlue};
      border-radius: 5px;

      ${HalfCircles} {
        ${Dash} {
         border-left: 1px dashed ${colors.white}; 
        }
      }

      ${Text}, ${CopyCode},${DescLine},${DateLine} {
        color: ${colors.white};
      }
    `
      : css`
          background: ${colors.white};
        `};

  ${({ type }) =>
    type === "small"
      ? css`
          // small COUPON ITEM type
          ${Container} {
            padding-left: 40px;
          }
        `
      : css`
          transition: 1.45s cubic-bezier(0.075, 0.82, 0.165, 1);

          // large COUPON ITEM type
          ${HalfCircles} {
            width: 24px;
            left: 80px;
            height: calc(100% + 32px);

            ${HalfCircle} {
              width: 24px;
              height: 24px;
            }

            ${Dash} {
              display: none;
            }

            ${minQuery(
        SizeMin.Medium,
        css`
                left: 104px;

                ${Dash} {
                  display: block;
                }

                ${Dash} {
                  height: calc(100% - 80px);
                }
              `,
      )};
          }

          ${Container} {
            min-height: 132px;
            flex-direction: column;
            justify-content: space-between;
            align-items: stretch;
            padding: 12px;
            padding-left: 12px;

            ${Logo} {
              display: none;
            }

            ${HeadLine} {
              ${Text}, ${CopyCode} {
                font-weight: 600;
                font-size: 20px;
              }
              ${CopyCodeIcon} {
                width: 15px;
                height: 18px;
              }
            }
            ${DescLine} {
              font-size: 14px;
              ${minQuery(
        SizeMin.MSmall,
        css`
                  overflow: hidden;
                  text-overflow: ellipsis;
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                `,
      )};
            }

            ${minQuery(
        SizeMin.Medium,
        css`
                padding-left: 152px;
                ${Logo} {
                  display: block;
                }

                ${Logo} {
                  left: 24px;
                }
              `,
      )};
          }

          &:hover {
            transform: translateY(-8px);
            box-shadow: 0 5px 0 20px rgba(0, 0, 0, 0.05);

            ${HalfCircles} {
              ${HalfCircle} {
                opacity: 0;
              }
            }
          }
        `}

  ${Container} {
    ${maxQuery(
        SizeMax.Small,
        css`
        ${HeadLine} {
          ${Text}, ${CopyCode} {
            font-size: 16px;
          }
          ${CopyCodeIcon} {
            width: 16px;
            height: 16px;
          }
        }
        ${DescLine} {
          font-size: 12px;
        }
      `,
      )}
  }
`;

export const WrapApplyButton = styled.div``;

export const WrapDateLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${maxQuery(
  SizeMax.MSmall,
  css`
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;

      ${WrapApplyButton} {
        margin-top: 0.5em;
      }
    `,
)};
`;
